import '@/lib/helpers/devOnlyConsole'
import './sentry.ts'
import { setupChunkErrorHandler } from '@/lib/helpers/chunkErrorHandler.ts'

// Initialize chunk error handler before anything else
// This catches 404 errors on dynamically imported modules after deployments
setupChunkErrorHandler()

// Ask the browser to mark our origin's storage as "persistent" so it isn't
// evicted under quota pressure. With thousands of cached attachments per
// stage this matters — without persist, Chrome can clear the SW cache when
// the OS is under disk pressure and the user loses offline data silently.
// The call is idempotent and resolves with a boolean (granted/denied).
if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
  navigator.storage.persist().catch(() => {
    // Browsers gate `persist()` on engagement heuristics (installed PWA,
    // bookmark, frequent visits). Failure is fine — eviction is just more
    // aggressive in that case, not catastrophic.
  })
}

import { Capacitor } from '@capacitor/core'
import { onlineManager } from '@tanstack/react-query'
import { Network } from '@capacitor/network'

// Mark <html> as native for CSS (prevents body scroll on native platforms)
if (Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('native-platform')

  // Wire React Query's onlineManager to Capacitor's Network plugin.
  // WKWebView's navigator.onLine / browser online/offline events are unreliable,
  // so refetchOnReconnect never fires without this.
  onlineManager.setEventListener(setOnline => {
    const handler = Network.addListener('networkStatusChange', status => {
      setOnline(status.connected)
    })
    Network.getStatus().then(status => setOnline(status.connected))
    return () => {
      handler.then(h => h.remove())
    }
  })
}

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import routes from './routes/routes'
import './i18n'
import './index.css'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import * as Sentry from '@sentry/react'
import { queryClient } from '@/lib/api-client.ts'
import { idbPersister, pausePersist, resumePersist } from '@/lib/queryPersister.ts'
import { restoreCachedAttachments } from '@/lib/prefetch/offlineAttachmentCache'

const SEVEN_DAYS = 1000 * 60 * 60 * 24 * 7

const persistOptions = {
  persister: idbPersister,
  maxAge: SEVEN_DAYS,
  // Persist every 500ms to capture more queries (default is 1000ms)
  buster: '',
  dehydrateOptions: {
    // Persist all successful queries including infinite queries
    shouldDehydrateQuery: (query: {
      state: { status: string }
      queryKey: readonly unknown[]
      meta?: Record<string, unknown>
    }) => {
      if (query.meta?.persist === false) return false
      return query.state.status === 'success'
    },
  },
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

/**
 * Initialize the app by restoring the query cache from IndexedDB BEFORE
 * mounting the router. This ensures offline data is available to loaders
 * on cold start.
 */
async function initializeApp() {
  // Restore cache from IndexedDB before router loaders run
  try {
    const persistedClient = await idbPersister.restoreClient()
    if (persistedClient) {
      // Hydrate the query client with persisted data.
      // Pause persistence to prevent PersistQueryClientProvider from
      // dehydrating + serializing the entire cache on every setQueryData call.
      // Without this, restoring 500+ queries triggers 500+ full-cache
      // serialization cycles, causing WKWebView OOM on iOS cold start.
      const { clientState } = persistedClient
      if (clientState?.queries) {
        pausePersist()
        try {
          clientState.queries.forEach(query => {
            if (query.state.data !== undefined) {
              queryClient.setQueryData(query.queryKey, query.state.data)
            }
          })
        } finally {
          await resumePersist()
        }
        if (import.meta.env.DEV) {
          console.log(`[App] Restored ${clientState.queries.length} cached queries from IndexedDB`)
        }
      }
    }
  } catch (error) {
    console.error('[App] Failed to restore cache:', error)
  }

  // Restore offline attachment file URLs from Dexie (native platforms only)
  try {
    const restoredCount = await restoreCachedAttachments()
    if (import.meta.env.DEV && restoredCount > 0) {
      console.log(`[App] Restored ${restoredCount} cached attachment file URLs`)
    }
  } catch {
    // Non-critical — attachment restore failure doesn't block app startup
  }

  // Now create and mount the router with cache available
  const router = sentryCreateBrowserRouter(routes)

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <RouterProvider router={router} />
      </PersistQueryClientProvider>
    </StrictMode>,
  )
}

initializeApp()
